/** A raw SSE frame, before the payload is interpreted. */
export type SseEvent = {
  event: string
  data: string
}

/**
 * An incremental SSE frame parser.
 *
 * Shared rather than owned by a feature: `EventSource` — GET only, no body — cannot read a
 * POST-with-body stream at all, so any consumer of one has to do the framing itself, and this is
 * the place to do it once. Bytes in, complete frames out, with no Angular and no network, which
 * is what makes the awkward cases cheap to test.
 *
 * Three real failure modes it has to get right. Chunk boundaries fall anywhere, so all state lives
 * on the instance and nothing is parsed before its terminating blank line has arrived. Multi-byte
 * characters split too, so one `TextDecoder` is held for the life of the stream and used with
 * `{stream: true}`. And a partial trailing frame is not a frame: `end()` discards it, because a
 * truncated stream is a crash, which a transport detects by the absence of its own terminal
 * frame rather than by guessing at half a frame.
 */
export class SseFrameParser {
  readonly #decoder = new TextDecoder('utf-8')
  #buffer = ''

  /** Feed one chunk; returns every frame completed by it (often none). */
  push(chunk: Uint8Array): SseEvent[] {
    this.#buffer += this.#decoder.decode(chunk, { stream: true })

    // Normalize line endings up front so frame splitting and line splitting below only ever
    // deal with "\n". SSE permits CRLF, and nginx is entitled to rewrite either.
    this.#buffer = this.#buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

    const blocks = this.#buffer.split('\n\n')

    // The last element is either an incomplete frame or "" when the buffer ended exactly on a
    // separator. Either way it is not yet ours to emit — keep it for the next chunk.
    this.#buffer = blocks.pop() ?? ''

    return blocks.map((block) => parseBlock(block)).filter((event): event is SseEvent => !!event)
  }

  /** Flush at end of stream. Any leftover is a frame whose terminating blank line never arrived,
   * so it is dropped rather than guessed at. */
  end(): void {
    const leftover = this.#buffer + this.#decoder.decode()
    this.#buffer = ''

    if (leftover.trim()) {
      console.warn('[sse] dropped a partial frame at end of stream', { leftover })
    }
  }
}

/** One block between blank lines, per the SSE grammar: `:`-leading lines are comments, a field may
 * appear with or without a value, and repeated `data:` fields concatenate with newlines. Unknown
 * fields are ignored rather than rejected. */
function parseBlock(block: string): SseEvent | null {
  const lines = block.split('\n').filter((line) => line.trim() && !line.startsWith(':'))

  const dataLines = lines
    .filter((line) => fieldName(line) === 'data')
    .map((line) => fieldValue(line))

  const eventLine = lines.find((line) => fieldName(line) === 'event')

  // A block of only comments carries nothing to dispatch on.
  if (!eventLine && !dataLines.length) {
    return null
  }

  return {
    // SSE's own default when a frame names no event. Servers here always name one; this
    // keeps a nameless frame parseable instead of dropping it silently.
    event: eventLine ? fieldValue(eventLine) : 'message',
    data: dataLines.join('\n'),
  }
}

function fieldName(line: string): string {
  const colon = line.indexOf(':')

  return colon === -1 ? line.trim() : line.slice(0, colon).trim()
}

function fieldValue(line: string): string {
  const colon = line.indexOf(':')

  if (colon === -1) {
    return ''
  }

  // Exactly one optional leading space after the colon is part of the framing, not the value.
  const raw = line.slice(colon + 1)

  return raw.startsWith(' ') ? raw.slice(1) : raw
}
